import getData from "@/__main__/get-data.mjs";
import TftAttributesV2 from "@/data-models/tft-attributes-v2.mjs";
import TftAugments from "@/data-models/tft-augments.mjs";
import TftAugmentsV2 from "@/data-models/tft-augments-v2.mjs";
import TftChampionsV2 from "@/data-models/tft-champions-v2.mjs";
import TftCharms from "@/data-models/tft-charms.mjs";
import TftEncounters from "@/data-models/tft-encounters.mjs";
import tftFilters from "@/data-models/tft-filters.mjs";
import TftItemsV2 from "@/data-models/tft-items-v2.mjs";
import TftLevelingMethods from "@/data-models/tft-leveling-methods.mjs";
import TftLootTables from "@/data-models/tft-loot-tables.mjs";
import TftPatches from "@/data-models/tft-patches.mjs";
import TftPowerups from "@/data-models/tft-powerups.mjs";
import TftUnlockables from "@/data-models/tft-unlockables.mjs";
import * as API from "@/game-tft/api.mjs";
import {
  isFutureSet,
  isRevivalSet,
  setPatches,
  showAugmentStats,
} from "@/game-tft/constants.mjs";
import getSetFiltersPatch from "@/game-tft/get-set-filters-patch.mjs";
import getStatFilters from "@/game-tft/get-stats-filters.mjs";
import fetchStatsDataV2 from "@/game-tft/tft-fetch-stats-v2.mjs";
import { getLocale } from "@/util/i18n-helper.mjs";

const V2StaticData = (
  set,
  options = {
    networkBackOffTime: 1000 * 60 * 60, // 1 hour
  },
) =>
  [
    getData(
      API.getChampionsV2(set),
      TftChampionsV2,
      ["tft", set, "champions"],
      options,
    ),
    getData(
      API.getTraitsV2(set),
      TftAttributesV2,
      ["tft", set, "traits"],
      options,
    ),
    getData(API.getItemsV2(set), TftItemsV2, ["tft", set, "items"], options),
    getData(
      API.getAugmentsV2(set),
      TftAugmentsV2,
      ["tft", set, "augments"],
      options,
    ),
    (set === "set11" || set === "set13") &&
      getData(
        API.getEncountersV2(set),
        TftEncounters,
        ["tft", set, "encounters"],
        options,
      ),
    set === "set12" &&
      getData(API.getCharmsV2(set), TftCharms, ["tft", set, "charms"], options),
    getData(API.getAugments(), TftAugments, ["tft", "augments"], options),
    getData(
      API.getLevelingMethods(),
      TftLevelingMethods,
      ["tft", "levelingMethods"],
      options,
    ),
    set === "set15" &&
      getData(
        API.getPowerups(set),
        TftPowerups,
        ["tft", set, "powerups"],
        options,
      ),
    (set === "set15" || set === "set16") &&
      getData(
        API.getLootTables(set),
        TftLootTables,
        ["tft", set, "lootTables"],
        options,
      ),
    set === "set16" &&
      getData(
        API.getUnlockables(set),
        TftUnlockables,
        ["tft", set, "unlockables"],
        options,
      ),
  ].filter(Boolean);

const localizations = (
  set,
  locale = "en",
  options = {
    networkBackOffTime: 1000 * 60 * 60, // 1 hour
  },
) =>
  [
    getData(
      API.getUnitsLocale(locale, set),
      TftChampionsV2,
      ["tft", set, "localizedUnits", locale],
      options,
    ),
    getData(
      API.getTraitsLocale(locale, set),
      TftAttributesV2,
      ["tft", set, "localizedTraits", locale],
      options,
    ),
    getData(
      API.getItemsLocale(locale, set),
      TftItemsV2,
      ["tft", set, "localizedItems", locale],
      options,
    ),
    getData(
      API.getAugmentsLocale(locale, set),
      TftAugmentsV2,
      ["tft", set, "localizedAugments", locale],
      options,
    ),
    set === "set15" &&
      getData(
        API.getPowerupsLocale(locale, set),
        TftPowerups,
        ["tft", set, "localizedPowerups", locale],
        options,
      ),
    set === "set16" &&
      getData(
        API.getUnlockablesLocale(locale, set),
        TftUnlockables,
        ["tft", set, "localizedUnlockables", locale],
        options,
      ),
  ].filter(Boolean);

const StatsData = (
  set,
  urlSearchParams,
  statFilters,
  options = {
    networkBackOffTime: 1000 * 60 * 60, // 1 hour
    skipSafetyCheck: true,
  },
) => [
  getData(
    isFutureSet(set) ? API.getLatestPBEPatch() : API.getLatestPatch(),
    TftPatches,
    isFutureSet(set) ? ["tft", "pbePatch"] : ["tft", "latestPatch"],
    options,
  ).then((res) => {
    let patch = res.patch;

    const fetchStats = (defaultMode, defaultRank) => {
      const dataSet =
        (urlSearchParams?.get("set") && `set${urlSearchParams?.get("set")}`) ||
        setPatches[patch] ||
        set;
      const selectedDiffSet =
        urlSearchParams?.get("set") && dataSet !== setPatches[patch];
      const hasPatchFromConst =
        selectedDiffSet &&
        Object.entries(setPatches).some(([_, s]) => s === dataSet);
      if (hasPatchFromConst) {
        patch = Object.entries(setPatches).filter(
          ([_, s]) => s === dataSet,
        )[0][0];
      }
      return Promise.all([
        fetchStatsDataV2({
          patch,
          page: "units",
          filters: statFilters,
          set: dataSet,
          options,
        }),
        fetchStatsDataV2({
          patch,
          page: "traits",
          filters: statFilters,
          set: dataSet,
          options,
        }),
        fetchStatsDataV2({
          patch,
          page: "items",
          filters: statFilters,
          set: dataSet,
          options,
        }),
        showAugmentStats &&
          fetchStatsDataV2({
            patch,
            page: "augments",
            filters: getStatFilters(
              urlSearchParams,
              "augments",
              isFutureSet(set),
              isRevivalSet(set),
            ),
            set: dataSet,
            options,
          }),
        fetchStatsDataV2({
          patch,
          page: "comps",
          filters: getStatFilters(
            urlSearchParams,
            "comps",
            isFutureSet(set),
            isRevivalSet(set),
            defaultMode,
            defaultRank,
          ),
          set: dataSet,
          options,
        }),
      ]);
    };

    if (isFutureSet(set)) {
      return fetchStats("ALL", "NORMAL");
    }

    return getData(
      API.getDefaultFilters(patch),
      tftFilters,
      ["tft", "defaultFilters"],
      options,
    ).then(({ default_mode, modes_default_rank }) => {
      const defaultMode = default_mode.mode;
      const defaultRank = modes_default_rank?.[defaultMode]?.rank;
      return fetchStats(defaultMode, defaultRank);
    });
  }),
];

function fetchData(urlSearchParams) {
  const locale = getLocale();
  const { set, statFilters } = getSetFiltersPatch(urlSearchParams);

  return Promise.all([
    ...V2StaticData(set),
    ...localizations(set, locale),
    StatsData(set, urlSearchParams, statFilters),
  ]);
}

export function fetchStaticDataV2(urlSearchParams, set, options) {
  const locale = getLocale();
  const { statFilters } = getSetFiltersPatch(urlSearchParams);

  return Promise.all(
    [
      ...V2StaticData(set),
      options?.skipLocalization ? null : localizations(set, locale),
      options?.skipStats ? null : StatsData(set, urlSearchParams, statFilters),
    ].filter(Boolean),
  );
}

export function fetchStaticDataAsync(urlSearchParams, set, options) {
  const locale = getLocale();
  const { statFilters } = getSetFiltersPatch(urlSearchParams);

  const fetchOptions = {
    networkBackOffTime: 1000 * 60 * 60, // 1 hour
    skipSafetyCheck: true,
  };

  return Promise.all(
    [
      ...V2StaticData(set, fetchOptions),
      ...localizations(set, locale, fetchOptions),
      options?.skipStats
        ? null
        : StatsData(set, urlSearchParams, statFilters, fetchOptions),
    ].filter(Boolean),
  );
}

export default fetchData;
